import PageBackTitle from "../../Component/PageBackTitle";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import Tooltip from "@mui/material/Tooltip";
import { TbRefresh } from "react-icons/tb";
import {
  Users,
  ShieldCheck,
  Clock,
  AlertTriangle,
  UserX,
  AlertOctagon,
  Stethoscope,
  CalendarDays,
  ClipboardList,
  Settings2,
} from "lucide-react";
import { hrRecordsApi } from "../../Services/hrRecordsApi";
import { formatDateOnlyIST } from "../hrRecordsHelpers";
import "../hrRecords.css";
import "../../tables/User.css";
import "../training/trainingDashboard.css";

function MedicalDashboardPage({ isSidebarExpanded }) {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const showSnackbar = (message, severity = "success") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const response = await hrRecordsApi.getMedicalComplianceDashboard();
      setDashboard(response);
    } catch (error) {
      console.error("Failed to load medical dashboard data:", error);
      showSnackbar("Failed to load medical dashboard", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totals = dashboard?.totals || {};
  const thisMonth = dashboard?.this_month || {};
  const byType = dashboard?.by_type || [];

  const kpiCards = [
    { key: "employees_covered", label: "Employees Covered", value: totals.employees_covered, icon: Users, tone: "blue" },
    { key: "active", label: "Active", value: totals.active, icon: ShieldCheck, tone: "green" },
    { key: "expiring_soon", label: "Expiring Soon", value: totals.expiring_soon, icon: Clock, tone: "amber" },
    { key: "expired", label: "Expired", value: totals.expired, icon: AlertTriangle, tone: "red" },
    { key: "unfit", label: "Unfit", value: totals.unfit, icon: UserX, tone: "slate" },
    { key: "overdue", label: "Overdue", value: totals.overdue, icon: AlertOctagon, tone: "red" },
  ];

  const statStripItems = [
    { label: "Total Records", value: totals.total_records },
    { label: "Due in 30 Days", value: totals.due_in_30 },
    { label: "Due in 60 Days", value: totals.due_in_60 },
    { label: "Due in 90 Days", value: totals.due_in_90 },
    { label: "Exams This Month", value: thisMonth.exams_this_month },
    { label: "Employees This Month", value: thisMonth.employees_this_month },
  ];

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-list-content">
          <div className="data-list-header">
            <div className="data-list-header-content">
              <PageBackTitle>Medical Dashboard</PageBackTitle>
              <div className="data-list-heading-content-right-side">
                <Tooltip title="Reminder Settings" arrow>
                  <button
                    type="button"
                    className="new-button hr-records-settings-button"
                    onClick={() => navigate("/hr-medical-reminder-settings")}
                  >
                    <Settings2 size={15} /> Settings
                  </button>
                </Tooltip>
                <Tooltip title="Refresh" arrow>
                  <button className="refresh-button" onClick={fetchData}>
                    <TbRefresh className="refresh-icon" />
                  </button>
                </Tooltip>
              </div>
            </div>
          </div>
          <div className="header-separator"></div>

          <div className="data-list-body">
            <div className="data-list-scroll-body hr-records-dashboard-scroll">
              {loading ? (
                <div className="loader-container">
                  <div className="loader"></div>
                  <div>Loading...</div>
                </div>
              ) : (
                <div className="htd-dashboard">
                  {dashboard && (
                    <>
                      <div className="htd-kpi-grid">
                        {kpiCards.map(({ key, label, value, icon: Icon, tone }) => (
                          <div className={`htd-kpi-card htd-tone-${tone}`} key={key}>
                            <div className="htd-kpi-card-top">
                              <div className="htd-kpi-icon">
                                <Icon />
                              </div>
                            </div>
                            <div className="htd-kpi-value">{value || 0}</div>
                            <div className="htd-kpi-label">{label}</div>
                          </div>
                        ))}
                      </div>

                      <div
                        className="htd-stat-strip"
                        style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}
                      >
                        {statStripItems.map((item) => (
                          <div className="htd-stat-chip" key={item.label}>
                            <div className="htd-stat-chip-value">
                              {item.value || 0}
                            </div>
                            <div className="htd-stat-chip-label">
                              {item.label}
                            </div>
                          </div>
                        ))}
                      </div>

                      <div className="htd-two-col">
                        <div className="htd-panel">
                          <div className="htd-panel-header">
                            <Stethoscope />
                            <div className="htd-panel-title">
                              Compliance by Medical Type
                            </div>
                          </div>
                          {byType.length ? (
                            <>
                              <div className="htd-standard-list">
                                {byType.map((row) => {
                                  const total = row.total || 0;
                                  const expiringSoon = row.expiring_soon || 0;
                                  const expired = row.expired || 0;
                                  const other = Math.max(
                                    total - expiringSoon - expired,
                                    0,
                                  );
                                  const pct = (n) =>
                                    total > 0 ? (n / total) * 100 : 0;
                                  return (
                                    <div key={row.medical_type}>
                                      <div className="htd-standard-row-head">
                                        <span className="htd-standard-name">
                                          {row.medical_type}
                                        </span>
                                        <span className="htd-standard-total">
                                          {total} record{total === 1 ? "" : "s"}
                                        </span>
                                      </div>
                                      <div
                                        className="htd-bar"
                                        title={`Expiring soon: ${expiringSoon} | Expired: ${expired}`}
                                      >
                                        {expiringSoon > 0 && (
                                          <div
                                            className="htd-bar-seg expiring"
                                            style={{ width: `${pct(expiringSoon)}%` }}
                                          />
                                        )}
                                        {expired > 0 && (
                                          <div
                                            className="htd-bar-seg expired"
                                            style={{ width: `${pct(expired)}%` }}
                                          />
                                        )}
                                        {other > 0 && (
                                          <div
                                            className="htd-bar-seg active"
                                            style={{ width: `${pct(other)}%` }}
                                          />
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              <div className="htd-legend">
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot active" />
                                  Active / Valid
                                </div>
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot expiring" />
                                  Expiring Soon
                                </div>
                                <div className="htd-legend-item">
                                  <span className="htd-legend-dot expired" />
                                  Expired
                                </div>
                              </div>
                            </>
                          ) : (
                            <div className="hr-records-empty">
                              No medical type data found.
                            </div>
                          )}
                        </div>

                        <div className="htd-panel">
                          <div className="htd-panel-header">
                            <CalendarDays />
                            <div className="htd-panel-title">This Month</div>
                          </div>
                          <div className="htd-this-month">
                            <div className="htd-this-month-item">
                              <span className="htd-this-month-value">
                                {thisMonth.exams_this_month || 0}
                              </span>
                              <span className="htd-this-month-label">
                                Examinations
                              </span>
                            </div>
                            <div className="htd-this-month-item">
                              <span className="htd-this-month-value">
                                {thisMonth.employees_this_month || 0}
                              </span>
                              <span className="htd-this-month-label">
                                Employees Examined
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <div className="hr-records-section">
                    <div className="htd-section-title-row">
                      <ClipboardList />
                      <div className="hr-records-section-title" style={{ marginBottom: 0 }}>
                        Medical Types Needing Attention
                      </div>
                    </div>
                    <div className="data-list-table">
                      {byType.length ? (
                        <table className="table multilevel-data">
                          <thead>
                            <tr>
                              <th>MEDICAL TYPE</th>
                              <th>EMPLOYEES</th>
                              <th>TOTAL RECORDS</th>
                              <th>EXPIRED</th>
                              <th>EXPIRING SOON</th>
                              <th>EARLIEST DUE</th>
                            </tr>
                          </thead>
                          <tbody className="data-list-scroll-bodys">
                            {byType.map((item) => (
                              <tr key={item.medical_type} className="hoverable-row">
                                <td>{item.medical_type || "NA"}</td>
                                <td>{item.employees || 0}</td>
                                <td>{item.total || 0}</td>
                                <td>
                                  <span
                                    className={`htd-count-pill ${
                                      item.expired ? "expired" : "zero"
                                    }`}
                                  >
                                    {item.expired || 0}
                                  </span>
                                </td>
                                <td>
                                  <span
                                    className={`htd-count-pill ${
                                      item.expiring_soon ? "expiring" : "zero"
                                    }`}
                                  >
                                    {item.expiring_soon || 0}
                                  </span>
                                </td>
                                <td>{formatDateOnlyIST(item.earliest_due)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="hr-records-empty">No medical data found.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert severity={snackbarSeverity}>{snackbarMessage}</MuiAlert>
      </Snackbar>
    </>
  );
}

export default MedicalDashboardPage;
